
import React from "react";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash } from "lucide-react";
import type { CustomerFormValidation } from "./useCustomerFormValidation";

interface ContactInfoFieldsProps {
  form: UseFormReturn<CustomerFormValidation>;
  cellFields: any[];
  emailFields: any[];
  appendCell: () => void;
  removeCell: (index: number) => void;
  appendEmail: () => void;
  removeEmail: (index: number) => void;
}

export function ContactInfoFields({
  form,
  cellFields,
  emailFields,
  appendCell,
  removeCell,
  appendEmail,
  removeEmail,
}: ContactInfoFieldsProps) {
  return (
    <>
      <FormItem className="mb-2">
        <FormLabel>Cell Numbers</FormLabel>
        <FormDescription>
          Add up to 5 numbers (optional).
        </FormDescription>
        <div className="space-y-2 flex flex-col">
          {cellFields.map((field, idx) => (
            <div className="flex items-center gap-2" key={field.id}>
              <Input
                {...form.register(`cell_numbers.${idx}.value`)}
                placeholder={`Number ${idx + 1}`}
                type="tel"
              />
              {cellFields.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeCell(idx)}
                  aria-label="Remove number"
                >
                  <Trash className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          {cellFields.length < 5 && (
            <Button
              type="button"
              variant="secondary"
              onClick={appendCell}
            >
              <Plus className="w-4 h-4" /> Add Number
            </Button>
          )}
        </div>
        <FormMessage>
          {form.formState.errors.cell_numbers?.[0]?.value?.message as string}
        </FormMessage>
      </FormItem>

      <FormItem>
        <FormLabel>Emails</FormLabel>
        <FormDescription>
          Add up to 4 emails (optional).
        </FormDescription>
        <div className="space-y-2 flex flex-col">
          {emailFields.map((field, idx) => (
            <div className="flex items-center gap-2" key={field.id}>
              <Input
                {...form.register(`emails.${idx}.value`)}
                placeholder={`Email ${idx + 1}`}
                type="email"
              />
              {emailFields.length > 1 && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeEmail(idx)}
                  aria-label="Remove email"
                >
                  <Trash className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          {emailFields.length < 4 && (
            <Button
              type="button"
              variant="secondary"
              onClick={appendEmail}
            >
              <Plus className="w-4 h-4" /> Add Email
            </Button>
          )}
        </div>
        <FormMessage>
          {form.formState.errors.emails?.[0]?.value?.message as string}
        </FormMessage>
      </FormItem>
    </>
  );
}
